import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import Header from "../../components/common/header/Header";
import PhoneInput, {
  parsePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Link, useNavigate } from 'react-router-dom';
import Footer from "../../components/common/footer/Footer";
import { toast } from "react-toastify";
import OtpInput from 'react-otp-input';

import { UserLogin, User_Otp_Verify } from "../../api/auth_api/Auth_Api";
import Loader from "../../loader/Loader";
import { get_Page_Seo } from "../../api/global/Global";
import { Helmet } from "react-helmet";
import { IMG_BASE_URL, WEB_DOMAIN_KEY } from "../../config/Config";

const User_Login = () => {
  const navigate = useNavigate()
  const [pageSeo, set_Page_Seo] = useState({})

  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("+91");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryCode, setCountryCode] = useState("IN"); // To track the selected country code
  const [isValid, setIsValid] = useState(false);

  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(false);
  const [otpTimer, setOtpTimer] = useState(5 * 60);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [cooldownTime, setCooldownTime] = useState(0);

  const formatTimer = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  // <-------------- OTP session timeout --------------->
  useEffect(() => {
      if (showOtpScreen) {
          if (otpTimer <= 0) {
              setOtpExpired(true);
              return;
          }
          const countdown = setInterval(() => {
              setOtpTimer(prev => prev - 1);
          }, 1000);
          return () => clearInterval(countdown);
      }
  }, [otpTimer, showOtpScreen]);

  // <-------------- OTP attempts and cooldown initialization & countdown --------------->
  useEffect(() => {
    if (mobileNumber) {
      const savedAttempts = localStorage.getItem(`otp_attempts_${mobileNumber}`);
      if (savedAttempts) {
        setOtpAttempts(parseInt(savedAttempts, 10));
      } else {
        setOtpAttempts(0);
      }

      const cooldownUntil = localStorage.getItem(`otp_cooldown_until_${mobileNumber}`);
      if (cooldownUntil) {
        const remaining = Math.ceil((parseInt(cooldownUntil, 10) - Date.now()) / 1000);
        if (remaining > 0) {
          setCooldownTime(remaining);
        } else {
          setCooldownTime(0);
          localStorage.removeItem(`otp_cooldown_until_${mobileNumber}`);
          localStorage.removeItem(`otp_attempts_${mobileNumber}`);
          setOtpAttempts(0);
        }
      } else {
        setCooldownTime(0);
      }
    }
  }, [mobileNumber, showOtpScreen]);

  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            localStorage.removeItem(`otp_cooldown_until_${mobileNumber}`);
            localStorage.removeItem(`otp_attempts_${mobileNumber}`);
            setOtpAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownTime, mobileNumber]);

  const formatMobileNumber = (mobile) => {
      if (!mobile || mobile.length < 10) return mobile;
      const firstTwoDigits = mobile.slice(0, 2);
      const lastTwoDigits = mobile.slice(-2);
      return `${firstTwoDigits}******${lastTwoDigits}`;
  };

  const Handle_User_Login_Otp_Verify = async (e) => {
      e.preventDefault();
      
      if (mobileNumber) {
          const cooldownKey = `otp_cooldown_until_${mobileNumber}`;
          const cooldownUntil = localStorage.getItem(cooldownKey);
          if (cooldownUntil && Date.now() < parseInt(cooldownUntil, 10)) {
              toast("Too many attempts. please try again later");
              return;
          }
      }

      if (otpExpired) {
          toast("OTP session expired. Please request a new OTP.");
          setShowOtpScreen(false);
          setOtpTimer(5 * 60);
          return;
      }
      setIsLoading(true);

      // Check if the OTP is valid (4 digits long)
      if (otp.length !== 4) {
          setOtpError(true);
          setIsLoading(false);
          toast("Please enter a valid 4-digit OTP.");
          return;
      }

      try {
          const otp_verify_data = {
              otp,
              type: "login",
              country_code: `+${countryCode}`,
              mobile: mobileNumber,
              device_type: "web",
          };

          const response = await User_Otp_Verify(otp_verify_data);

          if (response?.data?.status == "200") {
              setIsLoading(false);
              toast(response?.data?.message);
              if (mobileNumber) {
                  localStorage.removeItem(`otp_attempts_${mobileNumber}`);
                  localStorage.removeItem(`otp_cooldown_until_${mobileNumber}`);
              }
              localStorage.setItem("user_token", response?.data?.data?.token);
              localStorage.setItem("user_is_active", true);
              const redirectPath = localStorage.getItem("redirect_after_login");

              if (redirectPath) {
                  localStorage.removeItem("redirect_after_login");
                  navigate(redirectPath);
              } else {
                  navigate("/");
              }
          } else {
              setIsLoading(false);
              toast(response?.response?.data?.message || "Invalid OTP");
              
              if (mobileNumber) {
                  const newAttempts = otpAttempts + 1;
                  setOtpAttempts(newAttempts);
                  localStorage.setItem(`otp_attempts_${mobileNumber}`, newAttempts);
                  
                  if (newAttempts >= 3) {
                      localStorage.setItem(`otp_cooldown_until_${mobileNumber}`, Date.now() + 5 * 60 * 1000);
                      setCooldownTime(300);
                      toast("Too many attempts. please try again later");
                  }
              }
          }
      } catch (error) {
          setIsLoading(false);
          console.error("Error during OTP verification:", error);
          
          if (mobileNumber) {
              const newAttempts = otpAttempts + 1;
              setOtpAttempts(newAttempts);
              localStorage.setItem(`otp_attempts_${mobileNumber}`, newAttempts);
              
              if (newAttempts >= 3) {
                  localStorage.setItem(`otp_cooldown_until_${mobileNumber}`, Date.now() + 5 * 60 * 1000);
                  setCooldownTime(300);
                  toast("Too many attempts. please try again later");
              }
          }
      }
  };


  // <--------------get seo meta data----------------->
  useEffect(() => {
    const Get_Page_Seo = async () => {
      setIsLoading(true);
      try {
        const response = await get_Page_Seo()
        console.log("PageSeo:", response);
        if (response?.data?.status == 200) {
          const seo = response?.data?.data?.seo
          const filterSeoPage = seo?.find(page => page?.page_name === "User Login")
          set_Page_Seo(filterSeoPage)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false);
      }
    }

    Get_Page_Seo()
  }, [])

  // <----------- Onchnage Events on the Phone Input section's -------------->
  const handlePhoneChange = (value) => {

    if (!value || !value.startsWith("+")) {
      setPhoneNumber("+91");
      setCountryCode("91");
      setMobileNumber("");
      return;
    }

    setPhoneNumber(value);

    try {
      const parsedPhone = parsePhoneNumber(value);
      setCountryCode(parsedPhone.countryCallingCode);
      setMobileNumber(parsedPhone.nationalNumber);
    } catch (error) {
      setMobileNumber("");
    }
  };

  // <----------- Handle Key Down Events on the Phone Input section's -------------->
  const handleKeyDown = (e) => {
    // Prevent deleting the "+" or the country code
    if (
      (e.key === "Backspace" || e.key === "Delete") &&
      phoneNumber &&
      phoneNumber.length <= 3 // block when cursor is within "+91"
    ) {
      e.preventDefault();
    }
  };


  const HandleResendOtp = async () => {
    if (mobileNumber) {
      const cooldownKey = `otp_cooldown_until_${mobileNumber}`;
      const cooldownUntil = localStorage.getItem(cooldownKey);
      if (cooldownUntil && Date.now() < parseInt(cooldownUntil, 10)) {
        toast("Too many attempts. please try again later");
        return;
      }
    }
    setIsLoading(true);
    const user_login_data = {
      mobile: mobileNumber,
      country_code: `+${countryCode}`,
      user_role: 1,
    };
    try {
      const response = await UserLogin(user_login_data);
      if (response?.data?.status == "200") {
        setIsLoading(false);
        setOtpTimer(5 * 60);
        setOtpExpired(false);
        setOtp('');
        toast("OTP resent successfully");
      } else {
        setIsLoading(false);
        toast(response?.response?.data?.message || "Error resending OTP");
      }
    } catch (error) {
      setIsLoading(false);
      console.log("error", error);
    }
  };

  // <--------- User Login Section's here ----------------------->
  const HandleUserLogin = async (e) => {
    e.preventDefault();
    if (mobileNumber) {
      const cooldownKey = `otp_cooldown_until_${mobileNumber}`;
      const cooldownUntil = localStorage.getItem(cooldownKey);
      if (cooldownUntil && Date.now() < parseInt(cooldownUntil, 10)) {
        toast("Too many attempts. please try again later");
        return;
      }
    }
    setIsLoading(true)
    const user_login_data = {
      mobile: mobileNumber,
      country_code: `+${countryCode}`,
      user_role: 1,
    };
    if (!isValid) {
      toast("Please enter a valid phone number containing only numbers.");
      setIsLoading(false)
      return;
    }
    console.log("user_login_data", user_login_data);
    try {
      const response = await UserLogin(user_login_data);
      console.log("user-status:", response);

      if (response?.data?.status == "200") {
        setIsLoading(false)
        setShowOtpScreen(true);
        setOtpTimer(5 * 60);
        setOtpExpired(false);
        setOtp('');
      }
      else if (response?.response?.status == "500") {
        toast(response?.response?.data?.message)
        setIsLoading(false)
        setPhoneNumber("+91")

      } else if (response?.response?.data?.status == "500") {
        toast(response?.response?.data?.message)
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      console.log("error", error)
    }
  };

  // <-------- Apply for the Phone Number Validtion According to the Country and Country code here ----------->
  useEffect(() => {
    setIsValid(isValidPhoneNumber(phoneNumber));
  }, [phoneNumber]);


  return (
    <>
      <Helmet>
        <title>{pageSeo?.meta_title}</title>
        <meta name="description" content={pageSeo?.meta_description} />
        <link rel="canonical" href={`${WEB_DOMAIN_KEY}user-login`} />
        <link rel="alternate" hreflang="en-us" href={`${WEB_DOMAIN_KEY}user-login`} />

      </Helmet>
      <div>
        {/* <-------- ToastContainer ------------> */}
        {/* <------- Header section's ------------> */}
        <Header />
        <section className='banner-section'>
          <div className={`${pageSeo?.image ? "block" : "hidden"} relative`}>
            <img src={`${IMG_BASE_URL}${pageSeo?.image}`} alt={pageSeo?.alt
            } className='w-full h-[300px]' />


          </div>
        </section>
        {/* <------ User Login section's ---------> */}
        <section className="gi-register bg-[#F0F4F8] py-10 min-h-screen w-full overflow-x-hidden">
          {isLoading ? (
            <Loader />
          ) : (
            <div className="w-full px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap justify-between items-center mx-auto max-w-[720px] sm:max-w-[540px] bg-white p-5 rounded-lg">
                <div className="gi-login-box w-full px-4" id="login_modal">
                  {!showOtpScreen ? (
                    <>
                      <div className="section-title-2 w-full mb-5 pb-5">
                        <h1 className="gi-title mb-0 font-Poppins text-2xl font-semibold text-[#4b5966] relative inline capitalize leading-none">
                          Login Account
                        </h1>
                        <p className="max-w-[400px] mt-4 text-sm text-[#777] leading-[23px]">
                          If you have an account with us, please log in.
                        </p>
                      </div>

                      <div className="gi-login-wrapper mx-auto">
                        <div className="gi-login-container">
                          <div className="gi-login-form">
                            <form
                              onSubmit={HandleUserLogin}
                              className="flex flex-col space-y-4"
                            >
                              <div className="form-group">
                                <label htmlFor="phone" className="block text-sm font-medium text-[#4b5966] mb-1">
                                  Phone Number
                                </label>
                                <PhoneInput
                                  international
                                  defaultCountry="IN"
                                  value={phoneNumber}
                                  onChange={handlePhoneChange}
                                  onKeyDown={handleKeyDown}
                                  maxLength={15}
                                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md text-sm text-gray-700 outline-none focus:ring-2 focus:ring-red-400"
                                  placeholder="Enter phone number"
                                  id="phoneInput"
                                  autoFocus={true}
                                  inputProps={{
                                    autoFocus:true
                                  }}
                                />

                                {!isValid && (
                                  <span className="text-red-500 text-sm">
                                    Please enter a valid phone number
                                  </span>
                                )}
                              </div>

                              <div className="gi-login-wrap">
                                <button
                                  type="submit"
                                  id="showLink"
                                  className="w-full mt-4 p-3 text-center text-white bg-gradient-to-r from-[#9F2225] to-[#FFB500] rounded-md text-base font-medium"
                                >
                                  Send OTP
                                </button>
                              </div>

                              <div className="gi-login-wrap gi-login-btn mt-5 flex flex-row justify-center gap-2 items-center">
                                <span className="text-sm text-[#777]">
                                  Don't have an account?
                                </span>
                                <Link
                                  to="/user-register"
                                  className="text-sm font-semibold text-[#9F2225] hover:text-[#333] transition"
                                >
                                  Create Account
                                </Link>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="section-title-2 w-full mb-[20px] pb-[20px] text-center">
                          <h2 className="gi-title mb-[0] font-Poppins text-[26px] font-semibold text-[#4b5966] relative inline p-[0] capitalize leading-[1]">
                              Verification code
                          </h2>
                          <p className="mt-[15px] text-[14px] text-[#777] leading-[23px]">
                              We have sent the verification code on your mobile number +
                              <span className="font-bold text-black">{formatMobileNumber(mobileNumber)}</span>
                          </p>
                      </div>
                      <div className="gi-login-wrapper my-[0] mx-auto text-center">
                          <div className="gi-login-container">
                              <div className="gi-login-form">
                                  <form method="post" className="flex flex-col" onSubmit={Handle_User_Login_Otp_Verify}>
                                      <div className="gi-login-wrap flex-col">
                                          {/* OTP Input Fields */}
                                          <div className="otp-input-fields w-full flex justify-center gap-[10px] mb-[20px]">
                                              <OtpInput
                                                  value={otp}
                                                  onChange={(newOtp) => {
                                                      if (/^\d*$/.test(newOtp)) {
                                                          setOtp(newOtp);
                                                          if (newOtp.length === 4) {
                                                              setOtpError(false);
                                                          }
                                                      }
                                                  }}
                                                  numInputs={4}
                                                  shouldAutoFocus
                                                  renderInput={(props) => (
                                                      <input
                                                          {...props}
                                                          type="number"
                                                          className="otp__digit otp__field__1 rounded-[50%] border-[1px] border-[#ddd] p-[0] text-center text-[16px] text-[#333] focus:outline-none"
                                                          style={{ width: "40px", height: "40px" }}
                                                      />
                                                  )}
                                              />
                                          </div>
                                          {otpError && <span className="text-red-500 text-sm">Please enter a valid OTP.</span>}
                                      </div>
                                      <div className="text-center mb-[15px]">
                                          {cooldownTime > 0 ? (
                                              <span className="text-[14px] text-red-500 font-medium">
                                                  Too many attempts. Try again in {formatTimer(cooldownTime)}
                                              </span>
                                          ) : (
                                              <span className="text-[14px] text-[#777]">
                                                  {otpTimer > 0 ? `Resend OTP in ${formatTimer(otpTimer)}` : ""}
                                              </span>
                                          )}
                                      </div>
                                      <button
                                          type="button"
                                          onClick={HandleResendOtp}
                                          disabled={otpTimer > 0 || cooldownTime > 0}
                                          className={`w-full mb-[10px] block p-[14px] text-center rounded-[5px] text-[16px] outline-[0] h-[50px] ${
                                              otpTimer > 0 || cooldownTime > 0
                                                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                  : "text-[#fff] bg-gradient-to-r from-[#9F2225] to-[#FFB500]"
                                          }`}
                                      >
                                          Resend OTP
                                      </button>
                                      <button
                                          type="submit"
                                          disabled={cooldownTime > 0}
                                          className={`w-full mt-[20px] block p-[14px] text-center text-[#fff] rounded-[5px] text-[16px] outline-[0] h-[50px] ${
                                              cooldownTime > 0
                                                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                                  : "bg-gradient-to-r from-[#9F2225] to-[#FFB500]"
                                          }`}
                                      >
                                          Submit
                                      </button>
                                  </form>
                              </div>
                          </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
        {/* <-------- Footer section's ------------> */}
        <Footer />
      </div>
    </>
  );
};

export default User_Login;
